import * as React from "react"
import { Plus, TrendingUp, Users, Receipt, Pin, ChevronRight, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { CategorySummaryCard } from "@/components/expenses/category-summary-card"


import { ResponsiveGrid, ResponsiveContainer } from "@/components/layout/responsive-grid"
import { LoadingState } from "@/components/ui/loading-state"

import { useAuthStore } from "@/stores/auth.store"
import { useActiveGroup } from "@/stores/group.store"
import { useUserGroups } from "@/hooks/queries/useGroups"
import { useExpensesFlat } from "@/hooks/queries/useExpenses"
import { useGroupBalances } from "@/hooks/queries/useBalances"
import { useCategoryBalances } from "@/hooks/queries/useCategoryBalances"
import { useExpenseForm } from "@/contexts/expense-form.context"
import { useNavigate } from "react-router-dom"
import { logger } from '@/lib/logger'
import { formatCurrency } from '@/lib/utils'


export const DashboardPage: React.FC = () => {
  const { user } = useAuthStore()
  const { activeGroupId } = useActiveGroup()
  const { openAddExpenseForm } = useExpenseForm()
  const navigate = useNavigate()
  const [showQuickStats, setShowQuickStats] = React.useState(false)

  // Fetch real data from API
  const { data: groups = [], isLoading: isLoadingGroups, error: groupsError } = useUserGroups()
  const { data: expenses = [], isLoading: isLoadingExpenses, error: expensesError } = useExpensesFlat(
    activeGroupId || ""
  )
  const { data: balances, isLoading: isLoadingBalances } = useGroupBalances(activeGroupId || "")
  const { data: categoryBalancesData, isLoading: isLoadingCategoryBalances } = useCategoryBalances(activeGroupId || "")

  // Debug logging
  React.useEffect(() => {
    logger.debug('Dashboard balances:', { balances })
    logger.debug('Active group ID:', { activeGroupId })
  }, [balances, activeGroupId])

  const isLoading = isLoadingGroups || isLoadingExpenses || isLoadingBalances || isLoadingCategoryBalances
  const categoryBalances = categoryBalancesData?.categories || []
  const groupCurrency = expenses[0]?.currency || expenses[0]?.currency_code || 'EUR'

  // Calculate summary data from real expense data
  const summaryData = React.useMemo(() => {
    if (!expenses.length || !user) {
      return {
        totalExpenses: 0,
        userExpenses: 0,
        userOwes: 0,
        userOwed: 0,
        expenseCount: 0,
        topSpender: null,
        recentExpenses: [],
        categoryBreakdown: [],
      }
    }

    // Use total from balance API if available, otherwise calculate from loaded expenses
    const totalExpenses = balances?.totalExpenses || expenses.reduce((sum, expense) => sum + expense.amount, 0)
    const userExpenses = expenses.filter(expense => expense.paid_by_user_id === user.id)
      .reduce((sum, expense) => sum + expense.amount, 0)

    // Get recent expenses (last 5)
    const recentExpenses = expenses
      .sort((a, b) => {
        const dateA = new Date(a.date || a.created_at || 0)
        const dateB = new Date(b.date || b.created_at || 0)
        return dateB.getTime() - dateA.getTime()
      })
      .slice(0, 5)
      .map(expense => {
        // Try to find the user who paid
        let paidByName = 'Unknown'
        if (expense.user?.name) {
          paidByName = expense.user.name
        } else if (expense.paid_by_user?.name) {
          paidByName = expense.paid_by_user.name
        }

        return {
          id: expense.id,
          description: expense.description || 'Untitled expense',
          amount: expense.amount,
          currency: expense.currency || expense.currency_code || 'EUR',
          paidBy: {
            id: expense.paid_by || expense.paid_by_user_id || '',
            name: paidByName,
            email: expense.user?.email || expense.paid_by_user?.email
          },
          date: expense.date || expense.created_at,
        }
      })

    // Use net balances from API (already calculated on backend)
    return {
      totalExpenses,
      userExpenses,
      userOwes: balances?.balances?.owes || 0,
      userOwed: balances?.balances?.owed || 0,
      expenseCount: balances?.expenseCount || expenses.length,
      topSpender: null, // TODO: Calculate top spender from expenses
      recentExpenses,
      categoryBreakdown: [], // TODO: Calculate from expenses with categories
    }
  }, [expenses, user, balances])

  if (isLoading) {
    return <LoadingState message="Loading your dashboard..." />
  }

  if (groupsError || expensesError) {
    return (
      <ResponsiveContainer size="xl">
        <div className="text-center py-12">
          <h2 className="text-2xl font-semibold text-destructive mb-2">
            Failed to load dashboard data
          </h2>
          <p className="text-muted-foreground">
            Please try refreshing the page
          </p>
        </div>
      </ResponsiveContainer>
    )
  }

  return (
    <ResponsiveContainer size="xl">
      <div className="space-y-8">
        {/* Welcome Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Welcome back, {user?.name?.split(' ')[0] || 'there'}!</h1>
            <p className="text-muted-foreground mt-1">
              Here's what's happening with your expenses
            </p>
          </div>
          <Button onClick={openAddExpenseForm}>
            <Plus className="mr-2 h-4 w-4" />
            Add Expense
          </Button>
        </div>

        <div className="space-y-4">
          <Button
            variant="ghost"
            className="h-auto px-0 text-sm font-medium text-muted-foreground hover:bg-transparent hover:text-foreground"
            onClick={() => setShowQuickStats((current) => !current)}
          >
            {showQuickStats ? (
              <ChevronDown className="mr-2 h-4 w-4" />
            ) : (
              <ChevronRight className="mr-2 h-4 w-4" />
            )}
            {showQuickStats ? "Hide Overview" : "Show Overview"}
          </Button>

          {showQuickStats && (
            <ResponsiveGrid cols={{ default: 2, sm: 2, lg: 4 }}>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Expenses</CardTitle>
                  <Receipt className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{formatCurrency(summaryData.totalExpenses, groupCurrency)}</div>
                  <p className="text-xs text-muted-foreground">
                    {summaryData.expenseCount} expenses
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Active Groups</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{groups.length}</div>
                  <p className="text-xs text-muted-foreground">
                    {groups.reduce((sum, group) => sum + (group.users?.length || 0), 0)} total members
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">You're Owed</CardTitle>
                  <TrendingUp className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(summaryData.userOwed, groupCurrency)}</div>
                  <p className="text-xs text-muted-foreground">
                    Amount others owe you
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">You Owe</CardTitle>
                  <TrendingUp className="h-4 w-4 text-red-600 rotate-180" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold text-red-600">{formatCurrency(summaryData.userOwes, groupCurrency)}</div>
                  <p className="text-xs text-muted-foreground">
                    Amount you owe others
                  </p>
                </CardContent>
              </Card>
            </ResponsiveGrid>
          )}
        </div>

        {/* Pinned Categories */}
        {categoryBalances.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Pin className="h-4 w-4 text-muted-foreground" />
              <h2 className="text-lg font-semibold">Pinned Categories</h2>
            </div>
            <div className="space-y-3">
              {categoryBalances.map(category => (
                <CategorySummaryCard
                  key={category.categoryId}
                  categoryName={category.categoryName}
                  categoryEmoji={category.categoryEmoji}
                  balances={category.balances}
                  currencyCode={groupCurrency}
                />
              ))}
            </div>
          </div>
        )}

        {/* Recent Activity & Summary */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Recent Expenses */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Recent Expenses</CardTitle>
                    <CardDescription>
                      Your latest expense activity
                    </CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={() => navigate('/expenses')}>
                    View All
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {summaryData.recentExpenses.length > 0 ? (
                  summaryData.recentExpenses.map((expense) => (
                    <div
                      key={expense.id}
                      className="flex items-center justify-between p-3 rounded-lg border"
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Receipt className="h-4 w-4 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium">{expense.description}</div>
                          <div className="text-sm text-muted-foreground">
                            Paid by {expense.paidBy.name}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold">{formatCurrency(expense.amount, expense.currency)}</div>
                        <div className="text-xs text-muted-foreground">
                          {expense.date ? new Date(expense.date).toLocaleDateString() : 'No date'}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    No recent expenses
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Active Groups */}
          <div>
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Active Groups</CardTitle>
                    <CardDescription>
                      Groups you're part of
                    </CardDescription>
                  </div>
                  <Badge variant="secondary">3</Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  {groups.length > 0 ? (
                    groups.slice(0, 3).map((group) => (
                      <div key={group.id} className="flex items-center gap-3 p-3 rounded-lg border">
                        <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <span className="text-lg">💰</span>
                        </div>
                        <div className="flex-1">
                          <div className="font-medium">{group.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {group.users?.length || 0} members
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-medium">
                            $0.00
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      No groups yet
                    </div>
                  )}
                </div>
                <Button variant="outline" className="w-full" onClick={() => navigate('/groups')}>
                  View All Groups
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ResponsiveContainer>
  )
}
